import {
  ArrowLeft2,
  ArrowRight2,
  Camera,
  LanguageCircle,
  VoiceCricle,
} from 'iconsax-react';

import { cn } from '@src/design-system/helpers';
import { messageContentToPreviewText } from '@shared/features/reminders/helpers/use-template-channel-content-drafts';
import {
  OrganizationPreviewAvatar,
  useCurrentOrganizationPreview,
} from '@src/shared/components/organization-preview-avatar';
import type { TMedia } from '@src/shared/types/media';

export type SmsPreviewProps = {
  className?: string;
  content?: string;
  attachments?: TMedia[];
};

function formatSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function isImage(m: TMedia) {
  return m.mime_type?.startsWith('image/') ?? false;
}

export function SmsPreview({
  className,
  content,
  attachments,
}: SmsPreviewProps) {
  const { name: organizationName } = useCurrentOrganizationPreview();
  const text = messageContentToPreviewText(content);
  const hasText = Boolean(text);
  const hasFiles = Boolean(attachments?.length);

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col bg-white [-webkit-font-smoothing:antialiased]',
        className
      )}
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
      }}
    >
      <header className='flex shrink-0 items-center border-b border-[#d1d1d6] bg-[#F9F9F9F0] px-1 py-5'>
        <button
          type='button'
          className='flex shrink-0 items-center px-1 py-1 text-gray-800'
          aria-label='Back'
        >
          <ArrowLeft2
            size={22}
            variant='Outline'
            color='#584777'
          />
          <span className='text-xs font-medium'>iMessage</span>
        </button>
        <div className='flex min-w-0 flex-1 gap-y-1 flex-col items-center justify-center px-1 py-0.5'>
          <OrganizationPreviewAvatar
            className='size-9 ring-1 ring-black/10'
            fallbackClassName='bg-gradient-to-br from-[#8bc34a] via-[#cddc39] to-[#795548] text-xs font-semibold text-white'
          />
          <div className='mt-0.5 flex max-w-full items-center gap-0.5'>
            <span className='truncate text-xs font-semibold text-black'>
              {organizationName || 'Organization'}
            </span>
            <ArrowRight2
              size={14}
              className='shrink-0 text-gray-800'
            />
          </div>
        </div>
        <div
          className='w-[76px] shrink-0'
          aria-hidden
        />
      </header>

      <div className='flex min-h-0 flex-1 flex-col bg-white'>
        <div className='pointer-events-auto flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-3 scrollbar-gray'>
          {hasText || hasFiles ? (
            <div className='ml-auto max-w-[78%]'>
              <div
                className='rounded-[18px] bg-[#007AFF] px-3 py-2.5 text-white shadow-sm'
                style={{
                  borderBottomRightRadius: 4,
                }}
              >
                {hasText ? (
                  <p className='whitespace-pre-wrap break-words text-sm leading-snug'>
                    {text}
                  </p>
                ) : null}
                {hasFiles ? (
                  <div className={cn('flex flex-col gap-2', hasText && 'mt-2')}>
                    {attachments!.map((m) =>
                      isImage(m) ? (
                        <a
                          key={m.id}
                          href={m.file}
                          target='_blank'
                          rel='noreferrer'
                          className='block max-h-32 overflow-hidden rounded-xl'
                        >
                          <img
                            src={m.file}
                            alt={m.name}
                            className='max-h-32 w-full object-cover'
                          />
                        </a>
                      ) : (
                        <a
                          key={m.id}
                          href={m.file}
                          target='_blank'
                          rel='noreferrer'
                          className='flex items-center gap-2 rounded-xl bg-white/18 px-2 py-2'
                        >
                          <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/25 text-[10px] font-bold text-white'>
                            DOC
                          </span>
                          <span className='min-w-0 flex-1 text-left'>
                            <span className='block truncate text-[15px] font-medium'>
                              {m.name}
                            </span>
                            <span className='text-[11px] text-white/85'>
                              {formatSize(m.size)}
                            </span>
                          </span>
                        </a>
                      )
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <p className='py-8 text-center text-[14px] text-[#8e8e93]'>
              No message
            </p>
          )}
        </div>

        <footer className='shrink-0 px-2 pb-2 pt-1.5'>
          <div className='flex items-end gap-2'>
            <button
              type='button'
              className='mb-0.5 flex size-8 items-center justify-center text-[#8e8e93]'
              aria-label='Camera'
            >
              <Camera
                size={26}
                variant='Bold'
                color='#8e8e93'
              />
            </button>
            <button
              type='button'
              className='mb-0.5 flex size-8 items-center justify-center'
              aria-label='Apps'
            >
              {/* <span className='flex size-7 items-center justify-center rounded-md bg-gradient-to-b from-[#0a84ff] to-[#0066dd] text-[13px] font-bold text-white shadow-sm'>
                A
              </span> */}
              <LanguageCircle
                size={26}
                variant='Bold'
                color='#8e8e93'
              />
            </button>
            <div className='mb-0.5 flex min-h-[32px] flex-1 items-center rounded-[20px] border border-[#c7c7cc] bg-white pl-3 pr-2'>
              <span className='flex-1 text-sm text-[#c7c7cc]'>iMessage</span>
              <VoiceCricle
                size={22}
                variant='Bold'
                className='shrink-0 text-[#8e8e93]'
              />
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
